import { useEffect, useState } from 'react';
import type { GetV2ProviderPayersApiArg } from '@store/generated/payers';

import { useGetAdminPayersQuery, useGetPayerByIdQuery, useGetPayersQuery } from './api';

/**
 * Options for usePayers hook - provides a simpler interface
 * that maps to the generated API args
 */
export interface UsePayersOptions {
  pageToken?: string | null;
  perPage?: number;
  search?: string | null;
}

/**
 * Hook to get payers with optional callback support
 * Uses cursor-based pagination via pageToken/nextPageToken
 */
export function usePayers(
  options: UsePayersOptions = {},
  queryOptions?: {
    skip?: boolean;
    pollingInterval?: number;
    refetchOnFocus?: boolean;
  },
) {
  // Map our simplified interface to the generated API args
  const args: GetV2ProviderPayersApiArg = {
    pageToken: options.pageToken,
    perPage: options.perPage,
    search: options.search,
  };

  const result = useGetPayersQuery(args, {
    skip: queryOptions?.skip,
    pollingInterval: queryOptions?.pollingInterval || 0,
    refetchOnFocus: queryOptions?.refetchOnFocus || false,
  });

  return {
    payers: result.data?.items || [],
    total: result.data?.total || 0,
    perPage: result.data?.per_page || 20,
    nextPageToken: result.data?.next_page_token || null,
    hasNextPage: !!result.data?.next_page_token,
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}

/** Staff-only STEDI directory search using the generated admin endpoint. */
export function useAdminPayers(options: UsePayersOptions = {}, queryOptions?: { skip?: boolean }) {
  const result = useGetAdminPayersQuery(
    {
      pageToken: options.pageToken,
      perPage: options.perPage,
      search: options.search,
    },
    { skip: queryOptions?.skip },
  );

  return {
    payers: result.data?.items ?? [],
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    isError: result.isError,
  };
}

/**
 * Hook to get a single payer by ID
 */
export function usePayer(payerId: string | null, options?: { skip?: boolean }) {
  const result = useGetPayerByIdQuery(
    { payerId: payerId ?? '' },
    {
      skip: !payerId || options?.skip,
    },
  );

  return {
    payer: result.data || null,
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook for payer search with debouncing
 * Shows list results when no search term, search results when search term is provided
 * Uses cursor-based pagination
 */
export function usePayerSearch(debounceMs: number = 300) {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [pageToken, setPageToken] = useState<string | null>(null);
  const perPage = 50;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Reset pagination when search changes
      setPageToken(null);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchTerm, debounceMs]);

  // Determine if we're searching or listing
  const isSearching = debouncedSearchTerm.length >= 2;

  const { payers, isLoading, isError, error, total, nextPageToken, hasNextPage, ...rest } =
    usePayers(
      {
        pageToken: isSearching ? null : pageToken, // Search always starts fresh
        perPage,
        search: isSearching ? debouncedSearchTerm : null,
      },
      {
        skip: false, // Always fetch - either list or search
      },
    );

  const loadNextPage = () => {
    if (nextPageToken) {
      setPageToken(nextPageToken);
    }
  };

  return {
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    payers,
    isLoading,
    isError,
    error,
    perPage,
    total,
    nextPageToken,
    hasNextPage,
    loadNextPage,
    isSearching,
    isFetching: rest.isFetching,
    refetch: rest.refetch,
  };
}
